import React, { useState, useEffect, useRef } from 'react';
import { Clock, Phone, MapPin, Truck, Package, CheckCircle2 } from 'lucide-react';
// Note: the legacy iframe-based `printReceipt` import was removed in favor of
// the standalone /print/:orderNumber route that triggers rawbt:base64 directly.
// See utils/printReceipt.js — kept as a fallback for now but no longer wired.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Kitchen = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  
  // Print-related states
  const [autoPrintEnabled, setAutoPrintEnabled] = useState(
    localStorage.getItem('kitchenAutoPrint') === 'true'
  );
  const [showPrintConfirmModal, setShowPrintConfirmModal] = useState(false);
  const [pendingPrintOrder, setPendingPrintOrder] = useState(null);
  
  // Notification system states
  const [newOrderIds, setNewOrderIds] = useState(new Set());
  const [seenOrderIds, setSeenOrderIds] = useState(() => {
    const saved = localStorage.getItem('seenKitchenOrders');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  const [notificationAudio] = useState(() => new Audio('/notification.mp3'));
  const seenOrderIdsRef = useRef(seenOrderIds);
  const ordersRequestIdRef = useRef(0);

  useEffect(() => {
    seenOrderIdsRef.current = seenOrderIds;
  }, [seenOrderIds]);

  useEffect(() => {
    fetchOrders();
    // Auto-refresh every 10 seconds
    const orderInterval = setInterval(fetchOrders, 10000);
    // Update clock every second
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 1000);
    
    return () => {
      clearInterval(orderInterval);
      clearInterval(timeInterval);
    };
  }, []);

  const fetchOrders = async () => {
    const requestId = ++ordersRequestIdRef.current;
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/active`);
      if (response.ok) {
        const data = await response.json();
        if (requestId !== ordersRequestIdRef.current) {
          return;
        }
        const activeOrders = data;
        
        // Detect new orders
        const currentOrderIds = new Set(activeOrders.map(o => o.orderNumber));
        const truelyNewOrders = activeOrders.filter(order => 
          order.status === 'pending' && !seenOrderIdsRef.current.has(order.orderNumber)
        );
        
        if (truelyNewOrders.length > 0) {
          // Add to new orders set for visual highlight
          setNewOrderIds(new Set(truelyNewOrders.map(o => o.orderNumber)));
          
          // Play notification sound 3 times
          playNotificationSound(3);
        }
        
        setOrders(activeOrders);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };
  
  // Play notification sound multiple times
  const playNotificationSound = (times) => {
    let playCount = 0;
    const playSound = () => {
      if (playCount < times) {
        notificationAudio.play().catch(err => console.log('Audio play failed:', err));
        playCount++;
        if (playCount < times) {
          setTimeout(playSound, 1500); // Wait 1.5s between plays
        }
      }
    };
    playSound();
  };

  // Toggle auto print setting
  const toggleAutoPrint = () => {
    const newValue = !autoPrintEnabled;
    setAutoPrintEnabled(newValue);
    localStorage.setItem('kitchenAutoPrint', newValue.toString());
  };

  // Opens the standalone /print/:orderNumber page in a new tab, which fires
  // rawbt:base64 directly (auto=rawbt) — bypasses the Android Chrome print
  // pipeline that produced microscopic output on PT-210 / RAWBT.
  const handlePrintConfirm = (shouldPrint) => {
    if (shouldPrint && pendingPrintOrder) {
      window.open(`/print/${pendingPrintOrder.orderNumber}?auto=rawbt`, '_blank');
    }
    setShowPrintConfirmModal(false);
    setPendingPrintOrder(null);
  };

  const updateOrderStatus = async (orderNumber, newStatus) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/orders/${orderNumber}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (response.ok) {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2/LDciUFLIHO8tiJNwgZaLvt559NEAxQp+PwtmMcBjiR1/LMeSwFJHfH8N2QQAoUXrTp66hVFApGn+DyvmwhBTGH0fPTgjMGHm7A7+OZSA0PVqzn77BdGAg+ltryxnMpBSh+zPLaizsIGGS57OihUBELTKXh8bllHAU2jdXz0IAzBhxuwevemlAODlOq5O+zYBoGPJPY8sp2KwUme8rx3I4+CRZiturqpVITC0mi4PGzXx0FMo/W88p5LQUndMvw2II=');
        audio.play().catch(() => {});
        fetchOrders();
      }
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  // Accept order with print option
  const handleAcceptOrder = async (order) => {
    try {
      // Mark as seen and stop notification
      const updatedSeen = new Set(seenOrderIds);
      updatedSeen.add(order.orderNumber);
      setSeenOrderIds(updatedSeen);
      localStorage.setItem('seenKitchenOrders', JSON.stringify([...updatedSeen]));
      
      // Remove from new orders
      const updatedNew = new Set(newOrderIds);
      updatedNew.delete(order.orderNumber);
      setNewOrderIds(updatedNew);
      
      // Stop notification sound
      notificationAudio.pause();
      notificationAudio.currentTime = 0;
      
      await updateOrderStatus(order.orderNumber, 'accepted');
      
      if (autoPrintEnabled) {
        // Auto print — opens /print/:orderNumber?auto=rawbt in a new tab,
        // which fires rawbt:base64 URI on Android and falls back to
        // window.print() via UI button on desktop.
        window.open(`/print/${order.orderNumber}?auto=rawbt`, '_blank');
      } else {
        // Ask before printing
        setPendingPrintOrder(order);
        setShowPrintConfirmModal(true);
      }
    } catch (error) {
      console.error('Error accepting order:', error);
      alert('Hiba történt a rendelés elfogadása során');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-red-500',
      accepted: 'bg-green-500',
      confirmed: 'bg-blue-500',
      preparing: 'bg-yellow-500',
      ready: 'bg-green-500',
      rejected: 'bg-red-600'
    };
    return colors[status] || 'bg-gray-500';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'ÚJ RENDELÉS',
      accepted: 'ELFOGADVA',
      confirmed: 'MEGERŐSÍTVE',
      preparing: 'KÉSZÍTÉS ALATT',
      ready: 'KÉSZ',
      rejected: 'VISSZAUTASÍTVA'
    };
    return labels[status] || status.toUpperCase();
  };

  const getDeliveryTypeLabel = (type) => {
    const labels = {
      takeaway: 'ELVITEL',
      delivery: 'HÁZHOZSZÁLLÍTÁS',
      dinein: 'HELYBEN'
    };
    return labels[type] || type;
  };

  const getOrderAge = (createdAt) => {
    const now = new Date();
    const created = new Date(createdAt);
    const diffMinutes = Math.floor((now - created) / 1000 / 60);
    
    if (diffMinutes < 1) return 'Most érkezett';
    if (diffMinutes === 1) return '1 perce';
    return `${diffMinutes} perce`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#00BCD4] mx-auto mb-4"></div>
          <p className="text-white text-xl">Betöltés...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#00BCD4] to-[#FFA726] rounded-2xl p-6 mb-6 shadow-2xl">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-4xl font-black text-white mb-2">KONYHAI KIJELZŐ</h1>
            <p className="text-xl text-white/90">
              <Clock className="inline w-6 h-6 mr-2" />
              {currentTime.toLocaleTimeString('hu-HU')}
            </p>
          </div>
          
          {/* Auto Print Toggle */}
          <div className="bg-white/20 backdrop-blur-sm rounded-xl p-4">
            <label className="flex items-center space-x-3 cursor-pointer">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={autoPrintEnabled}
                  onChange={toggleAutoPrint}
                  className="sr-only peer"
                />
                <div className="w-14 h-8 bg-gray-600 rounded-full peer peer-checked:bg-green-500 transition-colors"></div>
                <div className="absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-transform peer-checked:translate-x-6"></div>
              </div>
              <div>
                <p className="text-white font-bold">Auto nyomtatás</p>
                <p className="text-white/70 text-sm">új rendelésnél</p>
              </div>
            </label>
          </div>
          
          <div className="text-white text-right">
            <p className="text-3xl font-black">{orders.length}</p>
            <p className="text-lg">Aktív rendelés</p>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      {orders.length === 0 ? (
        <div className="text-center py-20">
          <Package className="w-20 h-20 text-gray-600 mx-auto mb-4" />
          <p className="text-2xl text-gray-400 font-bold">Nincs aktív rendelés</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {orders.map((order) => {
            const isNew = newOrderIds.has(order.orderNumber);
            return (
            <div
              key={order.orderNumber}
              className={`bg-gray-800 rounded-2xl overflow-hidden shadow-2xl border-4 transition-all duration-500 ${
                isNew 
                  ? 'border-red-500 animate-pulse shadow-red-500/50' 
                  : 'border-gray-700'
              }`}
            >
              {/* NEW ORDER BADGE */}
              {isNew && (
                <div className="bg-red-600 text-white text-center py-2 font-black text-lg animate-bounce">
                  🔔 ÚJ RENDELÉS!
                </div>
              )}
              
              {/* Order Header */}
              <div className={`${getStatusColor(order.status)} p-4 flex items-center justify-between`}>
                <div>
                  <h2 className="text-3xl font-black text-white">#{order.orderNumber}</h2>
                  <p className="text-white/90 text-lg font-bold">{getStatusLabel(order.status)}</p>
                </div>
                <div className="text-right text-white">
                  <p className="text-sm">{getOrderAge(order.createdAt)}</p>
                </div>
              </div>

              {/* Customer Info */}
              <div className="p-4 bg-gray-700 space-y-2">
                <div className="flex items-center text-white">
                  <Phone className="w-5 h-5 mr-3 text-[#00BCD4]" />
                  <div>
                    <p className="text-xl font-bold">{order.customerName}</p>
                    <p className="text-lg text-gray-300">{order.customerPhone}</p>
                  </div>
                </div>
                
                <div className="flex items-center text-white bg-gray-600 p-3 rounded-xl">
                  <Truck className="w-5 h-5 mr-3 text-[#FFA726]" />
                  <p className="text-lg font-bold">{getDeliveryTypeLabel(order.deliveryType)}</p>
                </div>
                
                {order.deliveryType === 'delivery' && order.deliveryAddress && (
                  <div className="flex items-start bg-amber-100 border-2 border-amber-400 p-4 rounded-xl shadow-lg">
                    <MapPin className="w-6 h-6 mr-3 mt-1 text-amber-700 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-amber-900 mb-1">📍 SZÁLLÍTÁSI CÍM:</p>
                      <p className="text-gray-900 font-semibold">{order.deliveryAddress}</p>
                      {order.deliveryZone && (
                        <p className="text-sm text-amber-800 mt-1">Zóna: {order.deliveryZone}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Items */}
              <div className="p-4 bg-gray-800">
                <h3 className="text-white font-bold mb-3 text-lg">TÉTELEK:</h3>
                <div className="space-y-3">
                  {order.items.map((item, index) => (
                    <div key={index} className="bg-white rounded-xl p-3 shadow-sm border-2 border-gray-200">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-3xl font-black text-[#FFA726]">
                          {item.quantity}x
                        </span>
                        <span className="text-xl font-bold text-gray-900">
                          {item.price * item.quantity} Ft
                        </span>
                      </div>
                      <p className="font-bold text-gray-900 text-lg leading-tight">
                        {item.name}
                      </p>
                      {item.configuration && item.configuration.options && item.configuration.options.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                          {item.configuration.options.join(', ')}
                        </p>
                      )}
                      {item.selectedOptions && item.selectedOptions.length > 0 && (
                        <p className="text-sm text-gray-600 mt-1">
                          + {item.selectedOptions.join(', ')}
                        </p>
                      )}
                      {item.selectedExtras && item.selectedExtras.length > 0 && (
                        <p className="text-sm text-blue-600 font-semibold mt-1">
                          EXTRÁK: {item.selectedExtras.join(', ')}
                        </p>
                      )}
                      {item.selectedSpice && item.selectedSpice !== 'Normál' && (
                        <p className="text-lg text-red-600 font-black mt-1">
                          🌶️ {item.selectedSpice.toUpperCase()}
                        </p>
                      )}
                      {item.customerNote && (
                        <div className="mt-2 p-3 bg-yellow-100 border-2 border-yellow-500 rounded-lg shadow-sm">
                          <p className="text-base text-yellow-900 font-black uppercase">
                            ⚠️ FONTOS MEGJEGYZÉS: {item.customerNote}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              {order.notes && (
                <div className="bg-yellow-100 border-2 border-yellow-400 rounded-xl p-3 mx-4 mb-4">
                  <p className="font-bold text-yellow-900 text-sm mb-1">RENDELÉS MEGJEGYZÉS:</p>
                  <p className="text-yellow-800 font-semibold">{order.notes}</p>
                </div>
              )}

              {/* Price Summary */}
              <div className="p-4 bg-gray-900 text-white">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-xl font-bold">VÉGÖSSZEG:</span>
                  <span className="text-3xl font-black text-[#FFA726]">
                    {order.total.toLocaleString()} Ft
                  </span>
                </div>
                {order.paymentMethod && (
                  <p className="text-center text-gray-400 text-sm">
                    {order.paymentMethod === 'cash' ? 'Készpénz' : 'Bankkártya'} átvételkor
                  </p>
                )}
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-2 gap-2 p-4">
                {order.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleAcceptOrder(order)}
                      className="col-span-1 py-3 bg-green-500 text-white font-bold text-lg rounded-xl hover:bg-green-600 transition-colors"
                    >
                      ✓ ELFOGADÁS
                    </button>
                    <button
                      onClick={() => updateOrderStatus(order.orderNumber, 'rejected')}
                      className="col-span-1 py-3 bg-red-500 text-white font-bold text-lg rounded-xl hover:bg-red-600 transition-colors"
                    >
                      ✗ VISSZAUTASÍTÁS
                    </button>
                  </>
                )}
                {(order.status === 'accepted' || order.status === 'confirmed') && (
                  <button
                    onClick={() => updateOrderStatus(order.orderNumber, 'preparing')}
                    className="col-span-2 py-3 bg-yellow-500 text-white font-bold text-lg rounded-xl hover:bg-yellow-600 transition-colors"
                  >
                    KÉSZÍTÉS KEZDÉSE
                  </button>
                )}
                {order.status === 'preparing' && (
                  <button
                    onClick={() => updateOrderStatus(order.orderNumber, 'ready')}
                    className="col-span-2 py-3 bg-green-500 text-white font-bold text-lg rounded-xl hover:bg-green-600 transition-colors"
                  >
                    KÉSZ
                  </button>
                )}
                {order.status === 'ready' && (
                  <button
                    onClick={() => updateOrderStatus(order.orderNumber, 'completed')}
                    className="col-span-2 py-3 bg-[#00BCD4] text-white font-bold text-lg rounded-xl hover:bg-[#00ACC1] transition-colors"
                  >
                    KIADVA
                  </button>
                )}
              </div>
            </div>
            );
          })}
        </div>
      )}

      {/* Print Confirmation Modal */}
      {showPrintConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 animate-fadeIn">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl animate-slideUp">
            <h2 className="text-2xl font-black text-gray-900 mb-4">Elfogadtad a rendelést</h2>
            <p className="text-lg text-gray-700 mb-6">Szeretnéd kinyomtatni a rendelést?</p>
            <div className="flex gap-4">
              <button
                onClick={() => handlePrintConfirm(true)}
                className="flex-1 py-4 bg-gradient-to-r from-[#00BCD4] to-[#00ACC1] text-white rounded-xl font-bold text-lg hover:shadow-xl transition-all"
              >
                🖨️ IGEN, NYOMTATÁS
              </button>
              <button
                onClick={() => handlePrintConfirm(false)}
                className="flex-1 py-4 bg-gray-200 text-gray-700 rounded-xl font-bold text-lg hover:bg-gray-300 transition-all"
              >
                ❌ NEM
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Kitchen;
